import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, Ban, Check, ShieldAlert, Calendar } from "lucide-react";
import { productBySlug, tierById, productImage } from "@/data/catalogue";
import { TierBadge } from "@/components/TierBadge";
import LeadForm from "@/components/LeadForm";
import { Button } from "@/components/ui/button";

export default function ProductDetail() {
  const { slug } = useParams();
  const product = productBySlug(slug);
  if (!product) return <Navigate to="/catalogue" replace />;

  const isRoadmap = product.tier === "roadmap";
  const tier = tierById(product.tier);

  return (
    <div className="mx-auto max-w-7xl px-5 py-12 lg:px-8 lg:py-16">
      <Link
        to="/catalogue"
        data-testid="back-to-catalogue"
        className="inline-flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" strokeWidth={1.5} /> Back to catalogue
      </Link>

      {/* Header */}
      <div className="mt-8 border-b border-border pb-10">
        <TierBadge tierId={product.tier} />
        <h1 className="mt-5 max-w-4xl font-display text-4xl font-bold leading-[1.08] tracking-tight sm:text-5xl">
          {product.name}
          {product.tm && <sup className="ml-1 text-lg">™</sup>}
        </h1>
        {product.tierNote && (
          <p className="mt-3 text-sm text-muted-foreground">{product.tierNote}</p>
        )}
        <p className="mt-6 max-w-3xl text-lg leading-relaxed text-muted-foreground">{product.tagline}</p>
      </div>

      {/* Mobile quick price + jump-to-form (hidden on desktop where the sidebar shows it) */}
      <div className="mt-6 flex items-center justify-between gap-4 border border-border bg-[hsl(222_47%_7%)] p-4 lg:hidden">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-widest-xs text-muted-foreground">
            {isRoadmap ? "Availability" : "From"}
          </div>
          <div className="mt-0.5 truncate font-mono text-sm font-medium text-[hsl(var(--brand))]">
            {isRoadmap ? "Register Interest" : product.price.split(";")[0]}
          </div>
        </div>
        <Button
          asChild
          data-testid="mobile-product-cta"
          className="h-11 shrink-0 rounded-sm bg-[hsl(var(--brand))] px-5 text-white hover:bg-[hsl(217_91%_53%)]"
        >
          <a href="#enquire">{isRoadmap ? "Register interest" : "Enquire"}</a>
        </Button>
      </div>

      {/* Media banner */}
      <figure className="relative mt-10 overflow-hidden border border-border bg-[hsl(222_47%_7%)]">
        <img
          src={productImage(product.slug)}
          alt={`${product.name} — BIGTEK governance imagery`}
          data-testid="product-image"
          className="h-56 w-full object-cover object-center opacity-90 sm:h-72 lg:h-80"
        />
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[hsl(222_47%_7%)] via-transparent to-transparent" />
        <figcaption className="absolute bottom-3 left-4 font-mono text-[10px] uppercase tracking-widest-xs text-white/70">
          Bigibila governance system — BIGTEK
        </figcaption>
      </figure>

      <div className="grid gap-12 pt-12 lg:grid-cols-[1fr_400px] lg:gap-16">
        {/* Left: narrative + form */}
        <div>
          <section>
            <h2 className="font-display text-2xl font-medium">Overview</h2>
            <p className="mt-4 text-base leading-relaxed text-muted-foreground">{product.description}</p>
          </section>

          <section className="mt-10 border border-border p-6 lg:p-8">
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest-xs text-muted-foreground">
              <Check className="h-4 w-4 text-emerald-400" strokeWidth={2} /> Best for
            </div>
            <p className="mt-3 text-base leading-relaxed">{product.bestFor}</p>
          </section>

          {product.enterpriseBoundary && (
            <section className="mt-8 border-l-2 border-[hsl(var(--brand))] bg-secondary/50 p-6">
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest-xs text-[hsl(var(--brand))]">
                <ShieldAlert className="h-4 w-4" strokeWidth={1.75} /> Enterprise licensing boundary
              </div>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{product.enterpriseBoundary}</p>
            </section>
          )}

          {/* Form */}
          <section id="enquire" className="mt-14 scroll-mt-24 border-t border-border pt-12">
            <h2 className="font-display text-2xl font-medium">
              {isRoadmap ? "Register your interest" : "Enquire about this product"}
            </h2>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-muted-foreground">
              {isRoadmap
                ? "This product is in active design. Register your interest to be notified as it progresses and to help shape its scope. No pricing is published for roadmap products."
                : "Tell us your context and we'll respond by email with next steps. No account or payment required — all engagements are proposal-based."}
            </p>
            <div className="mt-8">
              <LeadForm mode={isRoadmap ? "interest" : "enquiry"} product={product} />
            </div>
          </section>
        </div>

        {/* Right: specs */}
        <aside className="lg:sticky lg:top-24 lg:self-start">
          <div className="border border-border">
            {/* Pricing */}
            <div className="border-b border-border bg-[hsl(222_47%_7%)] p-6 text-white">
              <div className="text-[10px] uppercase tracking-widest-xs text-slate-400">
                {isRoadmap ? "Availability" : "Indicative pricing"}
              </div>
              {isRoadmap ? (
                <div className="mt-2 font-display text-2xl font-medium">Register Interest</div>
              ) : (
                <div className="mt-2 font-mono text-lg font-medium leading-snug text-white">
                  {product.price}
                </div>
              )}
              {!isRoadmap && (
                <p className="mt-3 text-xs leading-relaxed text-slate-400">
                  Indicative only. Subject to scope, excludes GST. Community-controlled pathway
                  available on application.
                </p>
              )}
            </div>

            {/* Governance boundaries */}
            <div className="p-6">
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest-xs text-muted-foreground">
                Governance boundaries
              </div>
              <ul className="mt-4 space-y-3">
                {product.boundaries.map((b, i) => {
                  const negative = /not|cannot|does not|do not|no /i.test(b.slice(0, 12)) || /cannot|does not|do not|not distributed|not required|not substitute|not manufacture|not transfer|not currently|not a |not production|not accredited|not autonomously|not automatically/i.test(b);
                  return (
                    <li key={i} className="flex items-start gap-3 text-sm leading-relaxed">
                      {negative ? (
                        <Ban className="mt-0.5 h-4 w-4 shrink-0 text-destructive" strokeWidth={1.75} />
                      ) : (
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" strokeWidth={1.75} />
                      )}
                      <span className="text-muted-foreground">{b}</span>
                    </li>
                  );
                })}
              </ul>
            </div>

            {/* Licence exclusions */}
            {product.licence && (
              <div className="border-t border-border bg-amber-500/5 p-6">
                <div className="flex items-center gap-2 text-xs uppercase tracking-widest-xs text-amber-400">
                  <ShieldAlert className="h-4 w-4" strokeWidth={1.75} /> Licence excludes
                </div>
                <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
                  {["No editable master", "No sublicensing", "No resale", "No AI training", "No unrestricted reuse"].map((x) => (
                    <li key={x} className="flex items-center gap-2">
                      <Ban className="h-3.5 w-3.5 text-destructive" strokeWidth={1.75} /> {x}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Tier context */}
            <div className="border-t border-border p-6">
              <div className="text-xs uppercase tracking-widest-xs text-muted-foreground">Tier</div>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{tier.description}</p>
            </div>
          </div>

          {/* Briefing CTA */}
          <div className="mt-4 border border-border p-6">
            <p className="text-sm leading-relaxed text-muted-foreground">
              Prefer to talk it through first? Book a 90-minute executive briefing.
            </p>
            <Button
              asChild
              variant="outline"
              data-testid="product-booking-cta"
              className="mt-4 w-full rounded-sm"
            >
              <Link to="/book-briefing">
                <Calendar className="mr-2 h-4 w-4" strokeWidth={1.5} /> Book a briefing
              </Link>
            </Button>
          </div>
        </aside>
      </div>
    </div>
  );
}
